import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PageResponse } from '@/lib/types';

export type ReferenceTakeCandidate = {
  referenceTakeId: string;
  arrangementId: string;
  sourceTakeId: string;
  contributedByDisplayName: string | null;
  durationSeconds: number | null;
  notes: string | null;
  submittedAt: string;
  status: 'PENDING';
};

export type ReferenceTakeStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export type AdminReferenceTakeResult = {
  referenceTakeId: string;
  arrangementId: string;
  sourceTakeId: string;
  contributedBy: string;
  status: ReferenceTakeStatus;
  durationSeconds: number | null;
  notes: string | null;
  submittedAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  rejectionReason: string | null;
  version: number;
};

const CANDIDATES_KEY = ['admin', 'reference-takes', 'candidates'] as const;
const CANDIDATE_KEY = ['admin', 'reference-takes', 'candidate'] as const;

export function useReferenceTakeCandidates(params: {
  page: number;
  size: number;
}): UseQueryResult<PageResponse<ReferenceTakeCandidate>> {
  return useQuery({
    queryKey: [...CANDIDATES_KEY, params] as const,
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('page', String(params.page));
      sp.set('size', String(params.size));
      return api.fetch<PageResponse<ReferenceTakeCandidate>>(`/admin/reference-takes?${sp}`);
    },
  });
}

export function useReferenceTakeCandidate(
  referenceTakeId: string,
): UseQueryResult<ReferenceTakeCandidate> {
  return useQuery({
    queryKey: [...CANDIDATE_KEY, referenceTakeId] as const,
    queryFn: () =>
      api.fetch<ReferenceTakeCandidate>(`/admin/reference-takes/${referenceTakeId}`),
    enabled: Boolean(referenceTakeId),
  });
}

export type ApproveReferenceTakeVariables = {
  referenceTakeId: string;
  expectedVersion: number;
};

export function useApproveReferenceTake(): UseMutationResult<
  AdminReferenceTakeResult,
  Error,
  ApproveReferenceTakeVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ referenceTakeId, expectedVersion }: ApproveReferenceTakeVariables) =>
      api.fetch<AdminReferenceTakeResult>(`/admin/reference-takes/${referenceTakeId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ expectedVersion }),
      }),
    onSuccess: (_result, { referenceTakeId }) => {
      void queryClient.invalidateQueries({ queryKey: [...CANDIDATES_KEY] });
      void queryClient.invalidateQueries({ queryKey: [...CANDIDATE_KEY, referenceTakeId] });
    },
  });
}

export type RejectReferenceTakeVariables = {
  referenceTakeId: string;
  expectedVersion: number;
  reason: string;
};

export function useRejectReferenceTake(): UseMutationResult<
  AdminReferenceTakeResult,
  Error,
  RejectReferenceTakeVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ referenceTakeId, expectedVersion, reason }: RejectReferenceTakeVariables) =>
      api.fetch<AdminReferenceTakeResult>(`/admin/reference-takes/${referenceTakeId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ expectedVersion, reason }),
      }),
    onSuccess: (_result, { referenceTakeId }) => {
      void queryClient.invalidateQueries({ queryKey: [...CANDIDATES_KEY] });
      void queryClient.invalidateQueries({ queryKey: [...CANDIDATE_KEY, referenceTakeId] });
    },
  });
}
